import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { TargetService } from '../sidenav/target.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { SeoService } from '../seo/seo.service';
import { navigationLinks } from '../sidenav/navigationLinks';
import { IntercomService } from '../services/intercom.service';
import { CoreTargetService } from '../core/services/target.service';
import { CoreCurrencyService } from '../core/services/currency.service';
import { LocalStorageService } from '../core/services/local-storage.service';
import { CoreUserService } from '../core/services/user.service';
import { AuthTokenService } from '../core/services/auth-token.service';
import { FORCE_REGION, USE_SEO_BASE } from '../core/http/http-context.tokens';
import { whiteLabelTarget } from '../utils/whiteLabel';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  public user: any = null;

  // here we will store all the routes as objects with {path: '...', privileges: '...'} , in order to get specific path from privileges
  routesWithPrivileges: any = [];

  constructor(
    private _http: HttpClient,
    private router: Router,
    private targetList: TargetService,
    private seoService: SeoService,
    private intercomService: IntercomService,
    private targetService: CoreTargetService,
    private currencyService: CoreCurrencyService,
    private localStorageService: LocalStorageService,
    private userService: CoreUserService,
    private authTokenService: AuthTokenService,
  ) {
    this.constructRoutesWithPrivileges();
  }

  sessionStorageService = this.localStorageService.withSession();

  private removeOrganicUrlFromLocalStorage: boolean = true;

  whiteLabelTarget = whiteLabelTarget;

  constructRoutesWithPrivileges() {
    for (let i = 0; i < navigationLinks.length; i++) {
      if (
        navigationLinks[i].children &&
        navigationLinks[i].children.length > 0
      ) {
        for (let j = 0; j < navigationLinks[i].children.length; j++) {
          let routerLink = navigationLinks[i].children[
            j
          ].routerLink?.startsWith('')
            ? navigationLinks[i].children[j].routerLink.replace('/', '')
            : navigationLinks[i].children[j].routerLink;

          this.routesWithPrivileges.push({
            path: routerLink,
            privileges: navigationLinks[i].children[j].privilege,
          });
        }
      } else {
        let routerLink = navigationLinks[i].routerLink?.startsWith('')
          ? navigationLinks[i]?.routerLink?.replace('/', '')
          : navigationLinks[i]?.routerLink;

        this.routesWithPrivileges.push({
          path: routerLink,
          privileges: navigationLinks[i].privilege,
        });
      }
    }
  }

  login(data: any) {
    this.localStorageService.remove('activeSidePanelFilters');
    this.constructRoutesWithPrivileges();

    this.userService.saveUserInfo(data['user']);

    this.authTokenService.setToken(data['user'].token);
    this.localStorageService.set('email', data['user'].email);
    this.localStorageService.set('role', data['user'].role);
    this.localStorageService.set('name', data['user'].name);
    this.localStorageService.set('description', data['user'].description);
    this.localStorageService.set('master', data['user'].master);
    this.localStorageService.set(
      'bugSubscription',
      data['user'].bugSubscription,
    );
    this.localStorageService.set(
      'seoSubscriptionObj',
      data['user'].seoSubscription,
    );
    this.localStorageService.set('seo_roles', data['user'].seo_roles);

    if (data['user'].ads_roles && data['user']?.ads_roles?.length > 0) {
      this.localStorageService.set('ads_roles', data['user'].ads_roles);
    }

    if (data['user'].signup_premium) {
      this.localStorageService.set('organic_signup_premium', true);
    }

    if (data['user'].stripeConfig) {
      this.localStorageService.set('stripeConfig', data['user'].stripeConfig);
    }

    const selectedTarget = data['user'].target?.split(',')[0];

    if (data['user'].role == 'master') {
      if (this.whiteLabelTarget) {
        this.targetService.setSelectedTarget(
          'target',
          this.whiteLabelTarget.target,
        );
        this.targetService.setSelectedTarget(
          'name',
          this.whiteLabelTarget.description,
        );
      } else {
        this.targetService.setSelectedTarget('target', 'douglas');
        this.targetService.setSelectedTarget('name', 'Douglas DE');
      }
    } else {
      this.targetService.setSelectedTarget('target', selectedTarget);
    }

    this.currencyService.setSelectedCurrency(data['user'].currency);

    let allUserTargetPrivileges: any;
    if (data['user'].userPrivilegesTest) {
      allUserTargetPrivileges = data['user'].userPrivilegesTest;
    } else {
      allUserTargetPrivileges = data['user'].userPrivileges;
    }

    this.localStorageService.set(
      'allUserTargetPrivileges',
      allUserTargetPrivileges,
    );

    let privileges = allUserTargetPrivileges.find((user: any) => {
      return user.shopID === selectedTarget;
    })?.priv;

    if (!allUserTargetPrivileges || allUserTargetPrivileges?.length == 0) {
      privileges = data['user'].userPrivileges;
    } else if (!privileges || privileges?.length == 0) {
      privileges = allUserTargetPrivileges?.[0]?.priv;
    }
    this.localStorageService.set('privileges', privileges);

    const seoSubscriptionList = data['user'].seoSubscription;
    let seoSubscription = '';

    if (data['user'].role == 'master') {
      seoSubscription = 'Premium';
    } else {
      const seoSub =
        seoSubscriptionList?.find((sub: any) => {
          return sub.shopID == selectedTarget;
        })?.seoSubscription || 'Freemium';

      seoSubscription = seoSub || 'Freemium';
    }

    this.localStorageService.set('seoSubscription', seoSubscription);

    // Nudge Lite Detector
    this.localStorageService.set('nudgeLite', data['user'].ratio == 0.01);

    let region = '';
    // Region Check
    if (data['user'].region == 'central-us') {
      region = 'central-us';
    } else {
      region = 'west-eu';
    }
    // Set region in sessionStorage and localStorage
    this.sessionStorageService.set('region', region);
    this.localStorageService.set('region', region);

    if (data.phoneNumber) {
      this.localStorageService.set('user-verify-phone', true);
      this.removeOrganicUrlFromLocalStorage = true;
    } else if (
      !data.phoneNumber &&
      this.localStorageService.get('signup_organic_url') ==
        'signup_organic_leads' &&
      data?.user?.seo_roles?.length == 0
    ) {
      this.localStorageService.set('user-verify-phone', true);
      this.removeOrganicUrlFromLocalStorage = false;
    } else {
      this.localStorageService.remove('user-verify-phone');
      this.removeOrganicUrlFromLocalStorage = true;
    }

    this.intercomService.initIntercom();
    this.isloggedIn = true;
  }

  logout() {
    /** *** TO BE REMOVED ***  after 1-2 weeks on production */
    localStorage.removeItem('auth_key');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('id');
    localStorage.removeItem('role');
    localStorage.removeItem('privileges');
    localStorage.removeItem('target');
    localStorage.removeItem('targetName');
    localStorage.removeItem('selectedTarget');
    localStorage.removeItem('description');
    localStorage.removeItem('rangeValue');
    localStorage.removeItem('currency');
    localStorage.removeItem('kpiSymbol');
    localStorage.removeItem('startDate');
    localStorage.removeItem('endDate');
    localStorage.removeItem('bugSubscription');
    localStorage.removeItem('master');
    localStorage.removeItem('selectedProject');
    localStorage.removeItem('socket_object');
    localStorage.removeItem('region');
    localStorage.removeItem('nudgeLite');
    localStorage.removeItem('seo_roles');
    localStorage.removeItem('ads_roles');
    localStorage.removeItem('seoSubscriptionObj');
    localStorage.removeItem('seoSubscription');
    localStorage.removeItem('activeTab');
    localStorage.removeItem('activeTabAiAnalysis');
    localStorage.removeItem('organic_onboarding_redirect');
    localStorage.removeItem('customerId');
    localStorage.removeItem('activeSidePanelFilters');
    localStorage.removeItem('is_7_days_free_trial');
    localStorage.removeItem('user-verify-phone');
    localStorage.removeItem('showFullScreenBilling');
    /** *** TO BE REMOVED ***  after 1-2 weeks on production */

    this.localStorageService.clearNamespaceExcept([
      'darkMode',
      'language',
      'seenTours',
    ]);
    sessionStorage.clear();

    // Intercom
    this.intercomService.shutdown();

    this.isloggedIn = false;
    this.user = null;
    this.router.navigateByUrl('/login');
  }

  authenticated(email: string, password: string, twoFARequired?: string) {
    const payload: any = {
      email: email,
      password: password,
    };

    if (twoFARequired) {
      payload.twoFARequired = twoFARequired;
    }
    return this._http.post('/authenticate', payload, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  resetRequest(email: string) {
    return this._http.post(
      '/resetPasswordRequest',
      { email: email },
      {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      },
    );
  }

  resetPassword(password: string, token: string, userID: string) {
    var params = '?token=' + token + '&userId=' + userID;
    return this._http.post(
      '/resetPassword' + params,
      { password: password },
      {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      },
    );
  }

  private redirectUrl: string = '/';
  private loginUrl: string = '/login';
  private isloggedIn: boolean = false;

  isUserLoggedIn(): boolean {
    this.isKey();
    return this.isloggedIn;
  }
  isKey() {
    const key = this.localStorageService.get('auth_key');

    if (key != null) {
      this.isloggedIn = true;
    } else {
      this.isloggedIn = false;
    }
  }

  private loginMoreInfo = new BehaviorSubject('');

  getLoginMoreInfo() {
    return this.loginMoreInfo.asObservable();
  }
  setLoginMoreInfo(info: string) {
    this.loginMoreInfo.next(info);
  }

  navigate(user?: any) {
    const organicSub = this.user?.seoSubscription?.find((sub: any) => {
      return sub.shopID == this.targetService.getSelectedTarget('target');
    });
    if (this.user?.stripeConfig && organicSub?.seoSubscription == 'Freemium') {
      this.router.navigate(['organic-billing']);
      return;
    }
    if (
      !user?.user?.is_7_days_free_trial &&
      this.localStorageService.get('user-verify-phone')
    ) {
      this.router.navigate(['verify-phone']);
      return;
    }
    if (user?.user?.is_7_days_free_trial) {
      this.localStorageService.set('is_7_days_free_trial', true);

      if (!user?.user?.phoneNumber) {
        this.router.navigate(['verify-number']);
        return;
      }
    }

    this.targetList
      .getCurrentUserRoles(this.localStorageService.get('email'))
      .subscribe({
        next: (data) => {
          if (data?.signup_premium) {
            this.localStorageService.set('organic_signup_premium', true);
          }

          if (this.removeOrganicUrlFromLocalStorage) {
            this.localStorageService.remove('signup_organic_url');
          }

          // const privileges = data['privileges'] || [];
          let allUserTargetPrivileges = data['privileges'] || [];
          if (data.userPrivilegesTest) {
            allUserTargetPrivileges = data['userPrivilegesTest'];
          } else {
            allUserTargetPrivileges = data['privileges'];
          }

          const selectedTarget = this.targetService.getSelectedTarget('target');

          let privileges =
            allUserTargetPrivileges.find((user: any) => {
              return user.shopID === selectedTarget;
            })?.priv || [];

          if (
            !allUserTargetPrivileges ||
            allUserTargetPrivileges?.length == 0
          ) {
            privileges = data['privileges'];
          } else if (!privileges || privileges?.length == 0) {
            privileges = allUserTargetPrivileges?.[0]?.priv;
          }

          // if user is not premium, doesn't have stripeconfig show full screen billing
          if (
            this.localStorageService.get('seoSubscription') != 'Premium' &&
            !user?.stripeConfig
          ) {
            this.localStorageService.set('showFullScreenBilling', true);
          } else {
            this.localStorageService.remove('showFullScreenBilling');
          }

          let seoLinksIndex = 0;
          if (privileges.length > 0) {
            for (let i = 0; i < privileges.length; i++) {
              if (privileges[i].includes('seo')) {
                seoLinksIndex += 1;
              }
            }
            if (seoLinksIndex == privileges.length) {
              let tmpUser = user?.user;
              if (tmpUser && tmpUser.totalAnalysis) {
                this.seoService.getProjects().subscribe({
                  next: (res) => {
                    if (
                      this.localStorageService.get('seoSubscription') !=
                        'Premium' &&
                      this.localStorageService.get('organic_signup_premium') &&
                      this.localStorageService.get('email') !=
                        'master@behamics.com'
                    ) {
                      this.router.navigate(['/seo/billing']);
                    } else {
                      if (!res || res?.length == 0) {
                        this.router.navigate(['organic_onboarding']);
                      } else {
                        this.router.navigate(['seo/getting-started']);
                      }
                    }
                  },
                  error: (err) => {},
                });
              } else {
                if (
                  this.localStorageService.get('seoSubscription') !=
                    'Premium' &&
                  this.localStorageService.get('organic_signup_premium') &&
                  this.localStorageService.get('email') != 'master@behamics.com'
                ) {
                  this.router.navigate(['/seo/billing']);
                } else {
                  this.router.navigate(['seo/getting-started']);
                }
              }
            } else {
              this.router.navigate(['dashboard']);
            }
            this.setLoginMoreInfo('');

            // console.log(
            //   'getRouteFromPrivilege::: ',
            //   getRouteFromPrivilege(navigationLinks, privileges)
            // );
          } else {
            this.setLoginMoreInfo(
              'Login successful, but this account has no privileges. Please contact admin.',
            );
            this.logout();
          }
        },
        error: (e) => {
          this.setLoginMoreInfo(
            'Login successful, but this account has no privileges. Please contact admin.',
          );
          this.logout();
        },
      });
  }

  public getToken() {
    return this.localStorageService.get('auth_key');
  }

  getRedirectUrl(): string {
    return this.redirectUrl;
  }
  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }
  getLoginUrl(): string {
    return this.loginUrl;
  }
  logoutUser(): void {
    this.isloggedIn = false;
  }
  getCurrentUserRoles(email: any): Observable<any> {
    return this._http.get('/privileges/userPrivileges/user?email=' + email);
  }
  getMicrosoftLoginUrl(): Observable<any> {
    return this._http.get('/authenticate/azure/login');
  }
  signup(data: any): Observable<any> {
    return this._http.post('/signup', data);
  }

  pushAdsData(data: any): Observable<any> {
    return this._http.post('/ad-tracker-events', data);
  }

  addPhoneNumber(data: any): Observable<any> {
    return this._http.post('/add-phone-number', data);
  }
  getUserIP(): Observable<any> {
    return this._http.get('/get-user-ip');
  }
  adTrackerEvents(data: any): Observable<any> {
    return this._http.post('/ad-tracker-events', data);
  }

  getWebsiteTraffic(url: string): Observable<any> {
    return this._http.get('/analyses/get-website-traffic?url=' + url, {
      context: new HttpContext().set(USE_SEO_BASE, true),
    });
  }
}

declare global {
  interface Window {
    Intercom: any;
  }
}
